import { makeStyles } from '@material-ui/core/styles'

import { StagedAttachment } from '../../core/types/chat.types'
import { customScrollbarStyles } from '../ui/custom-scrollbar.styles'

/** Row budget per context: 4 rows in the composer, 8 in the Analyze surface (D-15). */
const MAX_HEIGHT_BY_ROWS = {
  4: 248,
  8: 504,
} as const

type AttachmentStagingAreaProps = {
  attachments: StagedAttachment[]
  error: string | null
  maxRows?: keyof typeof MAX_HEIGHT_BY_ROWS
  onRemove: (id: string) => void
}

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  panel: {
    background: 'var(--ds-bg-surface, #ffffff)',
    border: '1px solid var(--ds-border-default, #d7dae4)',
    borderRadius: 16,
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    overflowY: 'auto',
    ...customScrollbarStyles,
  },
  panelRows4: {
    maxHeight: MAX_HEIGHT_BY_ROWS[4],
  },
  panelRows8: {
    maxHeight: MAX_HEIGHT_BY_ROWS[8],
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    background: 'var(--ds-bg-subtle, #f1f2f7)',
  },
  iconHost: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'rgba(255,255,255,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'var(--ds-fg-secondary, #647089)',
  },
  meta: {
    flex: 1,
    minWidth: 0,
  },
  filename: {
    margin: 0,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondary: {
    margin: theme.spacing(0.25, 0, 0),
    fontSize: '0.75rem',
    color: 'var(--ds-fg-tertiary, #8c92a5)',
  },
  statusHost: {
    minWidth: 120,
    textAlign: 'right',
    fontSize: '0.75rem',
  },
  uploading: {
    color: '#b26a00',
  },
  queued: {
    color: 'var(--ds-fg-tertiary, #8c92a5)',
  },
  failed: {
    color: '#c54d39',
    fontWeight: 600,
  },
  ready: {
    color: '#17663a',
    fontWeight: 600,
  },
  removeButton: {
    border: 0,
    background: 'transparent',
    color: 'var(--ds-fg-secondary, #647089)',
    cursor: 'pointer',
    fontSize: '0.95rem',
    flexShrink: 0,
    padding: theme.spacing(0.5),
  },
  error: {
    margin: 0,
    paddingLeft: theme.spacing(0.5),
    fontSize: '0.75rem',
    color: '#c54d39',
  },
}))

const kindLabel = (kind: StagedAttachment['kind']) => {
  switch (kind) {
    case 'image':
      return 'Image'
    case 'document':
      return 'Document'
    case 'spreadsheet':
      return 'Spreadsheet'
    case 'text':
      return 'Text'
    default:
      return 'File'
  }
}

const formatSize = (bytes: number) => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`
  }
  return `${bytes} B`
}

const FileIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      fill="currentColor"
      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
      opacity="0.4"
    />
    <path fill="currentColor" d="M14 2v6h6" />
  </svg>
)

const AttachmentStagingArea = ({
  attachments,
  error,
  maxRows = 4,
  onRemove,
}: AttachmentStagingAreaProps) => {
  const classes = useStyles()
  const panelHeightClass = maxRows === 8 ? classes.panelRows8 : classes.panelRows4

  if (!attachments.length && !error) {
    return null
  }

  return (
    <div className={classes.root}>
      {attachments.length > 0 && (
        <div className={`${classes.panel} ${panelHeightClass}`}>
          {attachments.map((file) => (
            <div key={file.id} className={classes.row}>
              <div className={classes.iconHost}>
                <FileIcon />
              </div>
              <div className={classes.meta}>
                <p className={classes.filename} title={file.userFilename}>
                  {file.userFilename}
                </p>
                <p className={classes.secondary}>
                  {kindLabel(file.kind)} • {formatSize(file.size)}
                </p>
              </div>
              <div className={classes.statusHost}>
                {file.status === 'uploading' ? (
                  <span className={classes.uploading}>Uploading… {Math.round(file.progress)}%</span>
                ) : file.status === 'queued' ? (
                  <span className={classes.queued}>Queued</span>
                ) : file.status === 'error' ? (
                  <span className={classes.failed}>Failed</span>
                ) : (
                  <span className={classes.ready}>Ready</span>
                )}
                {file.status === 'error' && file.error ? <p className={classes.secondary}>{file.error}</p> : null}
              </div>
              <button
                type="button"
                className={classes.removeButton}
                onClick={() => onRemove(file.id)}
                aria-label={`Remove ${file.userFilename}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      {error ? <p className={classes.error}>{error}</p> : null}
    </div>
  )
}

export default AttachmentStagingArea
